import { useCallback } from 'react';
import type { ChangeEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@cbhq/cds-web/controls/Checkbox';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { PopoverPanel } from '@cbhq/cds-web/overlays';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';

import type { MemberRole, MemberRoleMap } from '../../utils/constants';

const messages = defineMessages({
  popoverAccessibilityLabel: {
    id: 'Team.inviteTray.productRolePopover.popoverAccessibilityLabel',
    defaultMessage: 'Change roles for {productLabel}',
    description: 'Accessibility label for the product role popover panel',
  },
  triggerAccessibilityLabel: {
    id: 'Team.inviteTray.productRolePopover.triggerAccessibilityLabel',
    defaultMessage: '{roleLabel}, roles for {productLabel}, click to change',
    description: 'Accessibility label for the popover trigger pressable',
  },
});

export type ProductRolePopoverParams = {
  roleOptions: MemberRoleMap[];
  selectedRoleValues: MemberRole[];
  onRoleToggle: (value: MemberRole, isSelected: boolean) => void;
  roleLabel: string;
  productLabel: string;
  disabled?: boolean;
};

const CONTENT_POSITION = { placement: 'bottom-end', gap: 1 } as const;
const TRIGGER_TEXT_BASE = {
  textDecorationLine: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: '4px',
} as const;
const TRIGGER_TEXT_ACTIVE = { ...TRIGGER_TEXT_BASE, opacity: 1 } as const;
const TRIGGER_TEXT_INACTIVE = { ...TRIGGER_TEXT_BASE, opacity: 0.6 } as const;

type RoleCheckboxItemParams = {
  role: MemberRoleMap;
  isSelected: boolean;
  onToggle: (value: MemberRole, isSelected: boolean) => void;
};

function RoleCheckboxItem({ role, isSelected, onToggle }: RoleCheckboxItemParams) {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onToggle(role.value, event.target.checked);
    },
    [onToggle, role.value],
  );

  return (
    <HStack as="label" width="100%" alignItems="center" gap={2} paddingX={3} paddingY={1}>
      <Checkbox
        value={role.value}
        checked={isSelected}
        onChange={handleChange}
        controlSize={22}
        borderRadius={0}
        background={isSelected ? 'fg' : undefined}
        borderColor={isSelected ? 'fg' : undefined}
      />
      <VStack gap={0} alignItems="flex-start" flexGrow={1}>
        <Text as="span" font="label1" color="fg" noWrap>
          {role.name}
        </Text>
        {role.description ? (
          <Text as="span" font="label2" color="fgMuted" noWrap>
            {role.description}
          </Text>
        ) : null}
      </VStack>
    </HStack>
  );
}

export function ProductRolePopover({
  roleOptions,
  selectedRoleValues,
  onRoleToggle,
  roleLabel,
  productLabel,
  disabled,
}: ProductRolePopoverParams) {
  const { formatMessage } = useIntl();

  const renderContent = useCallback(
    () => (
      <VStack gap={0} width="100%" paddingY={1}>
        {roleOptions.map((role) => (
          <RoleCheckboxItem
            key={role.value}
            role={role}
            isSelected={selectedRoleValues.includes(role.value)}
            onToggle={onRoleToggle}
          />
        ))}
      </VStack>
    ),
    [onRoleToggle, roleOptions, selectedRoleValues],
  );

  return (
    <PopoverPanel
      panelWidth={322}
      disabled={disabled}
      accessibilityLabel={formatMessage(messages.popoverAccessibilityLabel, { productLabel })}
      contentPosition={CONTENT_POSITION}
      content={renderContent}
    >
      <Pressable
        background="transparent"
        noScaleOnPress
        disabled={disabled}
        accessibilityLabel={formatMessage(messages.triggerAccessibilityLabel, {
          productLabel,
          roleLabel,
        })}
      >
        <Text
          as="span"
          font="label2"
          color="fgMuted"
          style={selectedRoleValues.length > 0 ? TRIGGER_TEXT_ACTIVE : TRIGGER_TEXT_INACTIVE}
        >
          {roleLabel}
        </Text>
      </Pressable>
    </PopoverPanel>
  );
}
